import { RefObject, useCallback, useEffect, useRef } from "react";
import { PaginatedPost } from "../../log.typing";
import { listen } from "./listen";

/** Older posts start loading this far before the top, so they are in place before the reader gets there. */
const LEAD_PX = 400;

const READER_INPUT_EVENTS = [
  "wheel",
  "touchstart",
  "touchmove",
  "pointerdown",
  "keydown",
];

/** The container is column-reverse: scrollTop is 0 at the bottom and negative going up. */
const distanceFromTop = (container: HTMLElement) =>
  container.scrollHeight - container.clientHeight + container.scrollTop;

type OlderPostsTriggerOptions = {
  canLoad: boolean;
  isLoading: boolean;
  onLoadOlder: () => void;
  shownPosts: PaginatedPost[];
};

/**
 * Asks for older posts when the reader scrolls near the top.
 *
 * A request is only armed by reader input, never by position alone. Otherwise a browser that leaves
 * the reader at the top edge after a page lands (momentum, overscroll bounce) chains page after page.
 */
export const useOlderPostsTrigger = (
  containerRef: RefObject<HTMLElement>,
  { canLoad, isLoading, onLoadOlder, shownPosts }: OlderPostsTriggerOptions
) => {
  // isLoading only turns true a render after the request, too late to stop the next scroll frame.
  const requested = useRef(false);
  const armed = useRef(true);

  const load = useCallback(() => {
    if (requested.current || isLoading || !canLoad) return;
    requested.current = true;
    onLoadOlder();
  }, [canLoad, isLoading, onLoadOlder]);

  // Declared before the fill check so a settled request is cleared before it runs.
  useEffect(() => {
    if (!isLoading) requested.current = false;
  }, [isLoading]);

  // A new machine, or the log reloaded after an edit.
  useEffect(() => {
    if (shownPosts.length === 0) armed.current = true;
  }, [shownPosts.length]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const loadIfNearTop = () => {
      if (!armed.current || distanceFromTop(container) > LEAD_PX) return;
      armed.current = false;
      load();
    };

    // Input is checked as well as scroll: at the very top, trying to scroll further fires no scroll event.
    const stopInput = listen(container, READER_INPUT_EVENTS, () => {
      armed.current = true;
      loadIfNearTop();
    });
    const stopScroll = listen(container, ["scroll"], loadIfNearTop);

    return () => {
      stopInput();
      stopScroll();
    };
  }, [containerRef, load]);

  // A log too short to scroll never produces the scroll that asks for more, so it is filled.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || isLoading || shownPosts.length === 0) return;
    if (container.scrollHeight - container.clientHeight < LEAD_PX) load();
    // Only when the posts on screen change - `load` changing on its own must not fire it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownPosts]);
};
